import type { Role } from "./types";

const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.cloudflare.com:3478" },
  { urls: "stun:stun.l.google.com:19302" },
];

const BUFFER_HIGH = 1 * 1024 * 1024;
const BUFFER_LOW = 256 * 1024;

export interface PeerEvents {
  onSdp: (sdp: RTCSessionDescriptionInit) => void;
  onIce: (candidate: RTCIceCandidateInit) => void;
  onConnected: () => void;
  onDisconnected: () => void;
  onControl: (raw: string) => void;
  onBulk: (data: ArrayBuffer) => void;
  onError: (err: unknown) => void;
}

export class Peer {
  private pc: RTCPeerConnection;
  private control?: RTCDataChannel;
  private bulk?: RTCDataChannel;
  private remoteDescSet = false;
  private pendingIce: RTCIceCandidateInit[] = [];
  private channelsOpen = 0;

  constructor(private role: Role, private events: PeerEvents) {
    this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    this.pc.addEventListener("icecandidate", (ev) => {
      if (ev.candidate) events.onIce(ev.candidate.toJSON());
    });

    this.pc.addEventListener("connectionstatechange", () => {
      const s = this.pc.connectionState;
      // "disconnected" is transient — ICE often recovers on its own.
      // Only react to terminal states.
      if (s === "failed" || s === "closed") {
        events.onDisconnected();
      }
    });

    if (role === "host") {
      this.control = this.pc.createDataChannel("control", { ordered: true });
      this.bulk = this.pc.createDataChannel("bulk", { ordered: true });
      this.bulk.binaryType = "arraybuffer";
      this.bulk.bufferedAmountLowThreshold = BUFFER_LOW;
      this.wireChannel(this.control);
      this.wireChannel(this.bulk);
    } else {
      this.pc.addEventListener("datachannel", (ev) => {
        const ch = ev.channel;
        if (ch.label === "control") this.control = ch;
        else if (ch.label === "bulk") {
          this.bulk = ch;
          ch.binaryType = "arraybuffer";
          ch.bufferedAmountLowThreshold = BUFFER_LOW;
        }
        this.wireChannel(ch);
      });
    }
  }

  private wireChannel(ch: RTCDataChannel): void {
    ch.addEventListener("open", () => {
      this.channelsOpen++;
      if (this.channelsOpen === 2) this.events.onConnected();
    });
    ch.addEventListener("close", () => this.events.onDisconnected());
    ch.addEventListener("error", (e) => this.events.onError(e));
    ch.addEventListener("message", (ev) => {
      if (ch.label === "control") {
        if (typeof ev.data === "string") this.events.onControl(ev.data);
      } else if (ch.label === "bulk") {
        if (ev.data instanceof ArrayBuffer) this.events.onBulk(ev.data);
      }
    });
  }

  async start(): Promise<void> {
    if (this.role !== "host") return;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    if (this.pc.localDescription) {
      this.events.onSdp(this.pc.localDescription.toJSON());
    }
  }

  async handleSdp(sdp: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(sdp);
    this.remoteDescSet = true;
    for (const c of this.pendingIce) {
      try {
        await this.pc.addIceCandidate(c);
      } catch (err) {
        this.events.onError(err);
      }
    }
    this.pendingIce.length = 0;

    if (sdp.type === "offer") {
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      if (this.pc.localDescription) {
        this.events.onSdp(this.pc.localDescription.toJSON());
      }
    }
  }

  async handleIce(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.remoteDescSet) {
      this.pendingIce.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(candidate);
    } catch (err) {
      this.events.onError(err);
    }
  }

  sendControl(raw: string): void {
    if (this.control?.readyState === "open") this.control.send(raw);
  }

  sendBulk(buf: ArrayBuffer): boolean {
    if (this.bulk?.readyState !== "open") return false;
    this.bulk.send(buf);
    return (this.bulk.bufferedAmount ?? 0) < BUFFER_HIGH;
  }

  async waitForDrain(): Promise<void> {
    const ch = this.bulk;
    if (!ch || ch.readyState !== "open") return;
    if (ch.bufferedAmount < BUFFER_HIGH) return;
    await new Promise<void>((resolve) => {
      const handler = () => {
        ch.removeEventListener("bufferedamountlow", handler);
        resolve();
      };
      ch.addEventListener("bufferedamountlow", handler);
    });
  }

  close(): void {
    try { this.control?.close(); } catch {}
    try { this.bulk?.close(); } catch {}
    try { this.pc.close(); } catch {}
  }
}
